import { useState } from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "app/shadcnComponents/ui/drawer";
import { Button } from "app/shadcnComponents/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { BestPracticeSection, GOAL_BEST_PRACTICE_DATA, GOAL_DRAWER_WIDTH } from "../../constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const iconMap: Record<BestPracticeSection["icon"], JSX.Element> = {
  clock: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      width={"20px"}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  "check-circle": (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      width={"20px"}
    >
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <path d="M22 4L12 14.01l-3-3" />
    </svg>
  ),
  edit: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      width={"20px"}
    >
      <path d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z" />
    </svg>
  ),
  activity: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      width={"20px"}
    >
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  "trending-up": (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      width={"20px"}
    >
      <path d="M23 6l-9.5 9.5-5-5L1 18" />
      <path d="M17 6h6v6" />
    </svg>
  ),
  tool: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      width={"20px"}
    >
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    </svg>
  ),
};

const GoalBestPracticeDrawer = ({ isOpen, onClose }: Props) => {
  const [openIndex, setOpenIndex] = useState<number | null>(
    GOAL_BEST_PRACTICE_DATA.sections.findIndex((s) => s.isDefaultExpanded)
  );

  const renderBoldText = (text: string) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) =>
      index % 2 === 1 ? <strong key={index}>{part}</strong> : part
    );
  };

  return (
    <Drawer direction="right" open={isOpen} onOpenChange={onClose}>
      <DrawerContent
        overlayClassName="bg-black/20"
        className={`right-0 top-0 left-auto bottom-auto mt-0 h-full ${GOAL_DRAWER_WIDTH} rounded-l-[10px] rounded-t-none figtree-container`}
      >
        <DrawerHeader className="d-flex justify-content-between align-items-center border-b pb-4">
          <DrawerTitle>{GOAL_BEST_PRACTICE_DATA.title}</DrawerTitle>
          <DrawerClose asChild>
            <Button variant="secondary-blue">
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="no-scrollbar overflow-y-auto px-4 py-4">
          {GOAL_BEST_PRACTICE_DATA.sections.map((section, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={section.id} className="mb-3">
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors bg-white text-left"
                  aria-expanded={isOpen}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                    {iconMap[section.icon]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
                      {section.title}
                    </h3>
                    <p className="text-xs text-gray-600">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 text-blue-600">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className={`transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </div>
                </button>
                {isOpen && (
                  <div className="mt-2 px-3 py-3 space-y-3">
                    {section.content.paragraphs.map((paragraph, pIndex) => (
                      <p
                        key={pIndex}
                        className="text-sm text-gray-700 leading-relaxed"
                      >
                        {renderBoldText(paragraph)}
                      </p>
                    ))}
                    {section.content.boxes.map((box, bIndex) => (
                      <div
                        key={bIndex}
                        className={`p-3 rounded-lg text-sm ${
                          box.type === "blue"
                            ? "bg-blue-50 border border-blue-100"
                            : "bg-amber-50 border border-amber-100"
                        }`}
                      >
                        <strong
                          className={
                            box.type === "blue"
                              ? "text-blue-900"
                              : "text-amber-900"
                          }
                        >
                          {box.label}
                        </strong>{" "}
                        <span
                          className={
                            box.type === "blue"
                              ? "text-blue-800"
                              : "text-amber-800"
                          }
                        >
                          {box.text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default GoalBestPracticeDrawer;
